import type { Flashcard } from './flashcard';

/**
 * Available exercise modes in the exercise engine
 */
export type ExerciseMode =
  | 'TYPE_IN'           // User types the answer
  | 'MULTIPLE_CHOICE'   // User selects from 4 options
  | 'MATCH_PAIRS'       // User matches pairs from two columns
  | 'LISTEN_TYPE'       // User listens to audio and types answer
  | 'CLOZE_SMART'       // User fills in the blank in a sentence
  | 'WORD_BUILD';       // User builds word from shuffled letters

/**
 * Extended flashcard type used as vocabulary item in exercises
 */
export type VocabItem = Flashcard;

/**
 * Direction of the exercise - which language is shown vs. expected
 */
export type ExerciseDirection = 'primary-to-target' | 'target-to-primary';

/**
 * A single exercise item in a session
 */
export interface ExerciseItem {
  /** Unique identifier for this exercise instance */
  id: string;
  /** The vocabulary item being tested */
  vocabItem: VocabItem;
  /** The exercise mode for this item */
  mode: ExerciseMode;
  /** Direction of the exercise */
  direction: ExerciseDirection;
  /** What the user sees (question/prompt) */
  prompt: string;
  /** The expected correct answer */
  correctAnswer: string;
  /** Mode-specific data */
  modeData: ExerciseModeData;
}

/**
 * Mode-specific data for different exercise types
 */
export type ExerciseModeData =
  | TypeInModeData
  | MultipleChoiceModeData
  | MatchPairsModeData
  | ListenTypeModeData
  | ClozeModeData
  | WordBuildModeData;

export interface TypeInModeData {
  type: 'TYPE_IN';
  /** Optional hint text */
  hint?: string;
  /** Transliteration if available */
  transliteration?: string;
  /** Phonetic transcription if available */
  phonetic?: string;
}

export interface MultipleChoiceModeData {
  type: 'MULTIPLE_CHOICE';
  /** Array of 4 options including the correct one */
  options: string[];
  /** Index of the correct option */
  correctIndex: number;
}

export interface MatchPairsModeData {
  type: 'MATCH_PAIRS';
  /** Left column items (prompts) */
  leftItems: { id: string; text: string }[];
  /** Right column items (answers) - shuffled order */
  rightItems: { id: string; text: string }[];
  /** Correct pairs mapping: leftId -> rightId */
  correctPairs: Record<string, string>;
}

export interface ListenTypeModeData {
  type: 'LISTEN_TYPE';
  /** Language code for TTS */
  languageCode: string;
  /** Text to be spoken */
  textToSpeak: string;
  /** Whether to show the written text as hint */
  showTextHint: boolean;
}

export interface ClozeModeData {
  type: 'CLOZE_SMART';
  /** Full sentence with _____ placeholder */
  sentenceWithGap: string;
  /** The word that fills the gap */
  gapWord: string;
  /** Position of the gap in the sentence */
  gapPosition: { start: number; end: number };
}

export interface WordBuildModeData {
  type: 'WORD_BUILD';
  /** Shuffled letters of the word */
  shuffledLetters: string[];
  /** The correct word to build */
  targetWord: string;
}

/**
 * User's answer payload for evaluation
 */
export interface UserAnswerPayload {
  /** The exercise item being answered */
  exerciseId: string;
  /** Mode of the exercise */
  mode: ExerciseMode;
  /** Mode-specific answer data */
  answer: UserAnswer;
  /** Time taken to answer in milliseconds */
  timeTaken: number;
}

export type UserAnswer =
  | TypeInAnswer
  | MultipleChoiceAnswer
  | MatchPairsAnswer
  | ListenTypeAnswer
  | ClozeAnswer
  | WordBuildAnswer;

export interface TypeInAnswer {
  type: 'TYPE_IN';
  text: string;
}

export interface MultipleChoiceAnswer {
  type: 'MULTIPLE_CHOICE';
  selectedIndex: number;
}

export interface MatchPairsAnswer {
  type: 'MATCH_PAIRS';
  /** User's pairs: leftId -> rightId */
  pairs: Record<string, string>;
}

export interface ListenTypeAnswer {
  type: 'LISTEN_TYPE';
  text: string;
}

export interface ClozeAnswer {
  type: 'CLOZE_SMART';
  text: string;
}

export interface WordBuildAnswer {
  type: 'WORD_BUILD';
  builtWord: string;
}

/**
 * Result of evaluating a user's answer
 */
export interface EvaluationResult {
  /** Whether the answer is correct */
  isCorrect: boolean;
  /** Similarity score (0-1) for partial credit */
  similarityScore: number;
  /** The correct answer for display */
  correctAnswer: string;
  /** User's normalized answer */
  normalizedUserAnswer: string;
  /** Feedback message */
  feedback: string;
  /** Whether a typo was detected but accepted */
  typoDetected: boolean;
  /** Suggested correction if typo detected */
  suggestedCorrection?: string;
}

/**
 * Configuration for generating exercise sessions
 */
export interface ExerciseSessionConfig {
  /** Total number of exercises to generate */
  totalExercises: number;
  /** Distribution of exercise modes (percentages, should sum to 100) */
  modeDistribution: Partial<Record<ExerciseMode, number>>;
  /** Preferred direction */
  direction: ExerciseDirection | 'mixed';
  /** Whether to allow repeated vocab items in different modes */
  allowRepeats: boolean;
  /** Typo tolerance threshold (0-1, 0 = exact match, 1 = very lenient) */
  typoTolerance: number;

  // FSRS options
  /** Use FSRS algorithm for intelligent card selection (prioritize due cards) */
  useFSRS?: boolean;
  /** Save exercise results as FSRS reviews (affects future card scheduling) */
  saveFSRSReview?: boolean;
  /** User ID for FSRS card selection and review tracking */
  userId?: string;
  /** Optional: Filter by target language (for FSRS card selection) */
  targetLanguageId?: string;
  /** Optional: Filter by categories (for FSRS card selection) */
  categoryIds?: string[];
}

/**
 * Default session configuration
 */
export const DEFAULT_SESSION_CONFIG: ExerciseSessionConfig = {
  totalExercises: 10,
  modeDistribution: {
    TYPE_IN: 40,
    MULTIPLE_CHOICE: 30,
    LISTEN_TYPE: 15,
    WORD_BUILD: 15,
  },
  direction: 'primary-to-target',
  allowRepeats: false,
  typoTolerance: 0.2,
  useFSRS: false,
  saveFSRSReview: false,
};

/**
 * A complete exercise session
 */
export interface ExerciseSession {
  /** Unique session identifier */
  id: string;
  /** List of exercises in this session */
  exercises: ExerciseItem[];
  /** Current exercise index */
  currentIndex: number;
  /** Session configuration used */
  config: ExerciseSessionConfig;
  /** Results for completed exercises */
  results: ExerciseResult[];
  /** Session start time */
  startedAt: Date;
  /** Session end time (if completed) */
  completedAt?: Date;
  /** Whether the session is complete */
  isComplete: boolean;
}

/**
 * Result of a single exercise in a session
 */
export interface ExerciseResult {
  /** Exercise item ID */
  exerciseId: string;
  /** Vocab item ID (flashcard ID) */
  vocabItemId: string;
  /** Exercise mode */
  mode: ExerciseMode;
  /** Whether answered correctly */
  isCorrect: boolean;
  /** User's raw answer */
  userAnswer: string;
  /** Correct answer */
  correctAnswer: string;
  /** Time taken in ms */
  timeTaken: number;
  /** Was answer skipped */
  skipped: boolean;
  /** Evaluation details */
  evaluation: EvaluationResult;
}

/**
 * Summary statistics for a completed session
 */
export interface SessionSummary {
  /** Total exercises in session */
  totalExercises: number;
  /** Number of correct answers */
  correctCount: number;
  /** Number of incorrect answers */
  incorrectCount: number;
  /** Number of skipped exercises */
  skippedCount: number;
  /** Score as percentage */
  scorePercentage: number;
  /** Total time taken in seconds */
  totalTimeTaken: number;
  /** Average time per exercise in seconds */
  averageTimePerExercise: number;
  /** Breakdown by mode */
  modeBreakdown: Record<ExerciseMode, { correct: number; total: number }>;
  /** XP earned */
  xpEarned: number;
}
